import React, {forwardRef} from 'react'

import {useController, UseControllerProps, useFormContext} from 'react-hook-form'

import Checkbox, {ICheckboxProps} from '../Checkbox/Checkbox'
import Input from '../Input'
import {IInputProps} from '../Input/Input'

interface IControlledInputProps
	extends Omit<ICheckboxProps, 'name' | 'value'>,
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		Omit<UseControllerProps<Record<string, any>>, 'onFocus' | 'defaultValue'> {}

const ControlledCheckbox = forwardRef<HTMLInputElement, IControlledInputProps>(
	(props: IControlledInputProps, forwardedRef) => {
		const {name, rules, ...inputProps} = props
		const formContext = useFormContext()
		const {
			control,
			formState: {errors},
		} = formContext
		const {field} = useController({name, control, rules})

		const onBlur: React.FocusEventHandler<HTMLInputElement> = (e) => {
			if (typeof field.onBlur === 'function')
				(field as {onBlur: React.FocusEventHandler<HTMLInputElement>}).onBlur(e)
			inputProps.onBlur && inputProps.onBlur(e)
		}

		const ChosenInput = Checkbox

		return (
			<ChosenInput
				{...inputProps}
				{...field}
				onBlur={onBlur}
				error={errors[name]?.message}
				ref={forwardedRef}
			/>
		)
	},
)

ControlledCheckbox.displayName = 'ControlledInput'

interface IFormTextInputProps extends IControlledInputProps {}

const FieldCheckbox = forwardRef<HTMLInputElement, IFormTextInputProps>((props, forwardedRef) => {
	const {name, ...inputProps} = props
	const formContext = useFormContext()

	if (!formContext || !name) {
		const errorMessage = !name
			? 'Form field must have a "name" prop!'
			: 'Form field must be a descendant of `FormProvider` as it uses `useFormContext`!'
		return <Input {...(inputProps as IInputProps)} error={errorMessage} />
	}

	return <ControlledCheckbox {...props} ref={forwardedRef} />
})

FieldCheckbox.displayName = 'FieldCheckbox'

export default FieldCheckbox
